import React from "react";
import { SectionLabel } from "./ui";

const TIERS = [
  {
    name: "Launch",
    audience: "SMBs · single workflow",
    price: "$199",
    cadence: "/mo + one-time build",
    build: "From $9.8K build · pay over 6 mo",
    features: ["1 production agent", "Up to 5 tool integrations", "10K agent actions / mo", "Shared infra · email support", "Live in 6 weeks"],
    cta: "Start with Launch",
  },
  {
    name: "Scale",
    audience: "Mid-market · multi-function",
    price: "$499",
    cadence: "/mo + one-time build",
    build: "From $28K build · pay over 12 mo",
    features: ["Up to 5 collaborating agents", "Unlimited tool integrations", "200K agent actions / mo", "Dedicated success engineer", "Custom guardrails + policies", "Quarterly KPI review"],
    cta: "Most teams pick this",
    featured: true,
  },
  {
    name: "Enterprise",
    audience: "Regulated · global · private",
    price: "Custom",
    cadence: "outcome-linked options",
    build: "Statement of work",
    features: ["Unlimited agents · agent fabric", "Private VPC / on-prem deploy", "SSO · SOC 2 · ISO 27001 · HIPAA", "24/7 incident response", "Dedicated AI architect team", "Co-developed roadmap"],
    cta: "Talk to sales",
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="relative bg-[#060c22] py-24 lg:py-32 border-y border-white/[0.05]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <SectionLabel tone="amber">Transparent pricing</SectionLabel>
          <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[44px] lg:text-[52px]">
            <span className="text-white">Priced for the outcome,</span>{" "}
            <span className="font-display italic grad-text">not the slideware.</span>
          </h2>
          <p className="mt-5 text-[16px] text-white/65 max-w-2xl">
            Pay a fixed build fee, then a predictable monthly run fee — and 80% of customers also opt into our outcome bonus (we earn more when you save more).
          </p>
        </div>
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {TIERS.map(t => (
            <div key={t.name} className={`relative rounded-3xl p-7 ${t.featured ? "border border-cyan-300/40 bg-gradient-to-b from-cyan-300/[0.05] to-white/[0.02] shadow-[0_0_0_1px_rgba(94,234,212,0.1),0_30px_80px_-30px_rgba(94,234,212,0.4)]" : "border border-white/[0.08] bg-white/[0.025]"}`}>
              {t.featured && (
                <div className="absolute -top-3 left-7">
                  <span className="rounded-full bg-cyan-300 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#051030]">Most popular</span>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <div className="text-[20px] font-semibold text-white">{t.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{t.audience}</div>
              </div>
              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="text-[42px] font-semibold tracking-tight text-white nums">{t.price}</span>
                <span className="text-[13px] text-white/55">{t.cadence}</span>
              </div>
              <div className="text-[12.5px] text-white/45 mt-1">{t.build}</div>
              <ul className="mt-6 space-y-2.5">
                {t.features.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-white/75">
                    <svg className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#book" className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-[13.5px] font-semibold ${t.featured ? "bg-white text-[#051030] hover:-translate-y-0.5" : "border border-white/15 text-white hover:bg-white/[0.07]"}`}>
                {t.cta}
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-[14px] text-white/75">
            <span className="font-semibold text-white">Outcome-linked option:</span> we cap your monthly fee until agreed KPIs hit, then share the upside. Available on Scale + Enterprise.
          </div>
          <a href="#book" className="font-mono text-[11.5px] uppercase tracking-[0.18em] text-cyan-300 hover:text-white">discuss outcomes pricing →</a>
        </div>
      </div>
    </section>
  );
}
